import { StickyNote } from 'lucide-react';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import {
	PunchupDialogue,
	DialogIcon,
	DialogTitle,
	DialogSubtitle,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupDetailText } from 'features/common/components/Punchup/composites/content/PunchupDetailText';
import { PunchupFeed, PunchupFeedItem } from 'features/common/components/Punchup/primitives/display/PunchupFeed';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import {
	useCreateFanNote,
	useDeleteFanNote,
	useFanNotes,
	usePartnerFanId,
	useUpdateFanNote,
} from 'features/PunchupPages/CustomFieldsResponse/hooks/useFanNotes';
import { useAutoFocusWhenVisible } from 'features/common/hooks/useAutoFocusWhenVisible';
import styles from './modals.module.css';

/**
 * Inline edit textarea, extracted so it can auto-focus on mount via the shared iframe-safe focus
 * hook (a hook can't be called inside the notes `.map()`). It remounts each time a different note
 * enters edit mode, so every edit-open re-focuses — matching the prior raw `autoFocus`, but deferred
 * a frame so the iframe height re-anchor can't steal it.
 */
const EditNoteTextarea: FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	useAutoFocusWhenVisible(textareaRef);
	return (
		<textarea
			ref={textareaRef}
			className={styles.editTextarea}
			value={value}
			onChange={(e) => onChange(e.target.value)}
		/>
	);
};

interface FanNotesModalProps {
	userId: number;
	email: string;
	fullName: string;
	onClose: () => void;
}

export const FanNotesModal: FC<FanNotesModalProps> = ({ userId, email, fullName, onClose }) => {
	const { t } = useTranslation();
	const [newNoteText, setNewNoteText] = useState('');
	const [noteSubmitted, setNoteSubmitted] = useState(false);
	const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
	const [editingText, setEditingText] = useState('');

	const partnerFanId = usePartnerFanId(userId);

	const { data: notes = [], isLoading } = useFanNotes(partnerFanId);
	// React Query v4 disabled queries (enabled: false) start with isLoading: true
	// (the "idle loading" state). Guard against that to prevent a permanent spinner
	// when partnerFanId is still being resolved.
	const notesLoading = !!partnerFanId && isLoading;
	const { createNote, isLoading: creating } = useCreateFanNote(partnerFanId, {
		onSuccess: () => {
			setNewNoteText('');
			setNoteSubmitted(false);
		},
	});
	const { updateNote, isLoading: updating } = useUpdateFanNote(partnerFanId, {
		onSuccess: () => setEditingNoteId(null),
	});
	const { deleteNote, isLoading: deleting } = useDeleteFanNote(partnerFanId);

	const handleEditStart = (noteId: number, text: string) => {
		setEditingNoteId(noteId);
		setEditingText(text);
	};

	const handleEditSave = () => {
		const trimmed = editingText.trim();
		if (editingNoteId !== null && trimmed) {
			updateNote({ noteId: editingNoteId, note: trimmed });
		}
	};

	const handleEditCancel = () => {
		setEditingNoteId(null);
		setEditingText('');
	};

	const handleAddNote = () => {
		setNoteSubmitted(true);
		const trimmed = newNoteText.trim();
		if (!trimmed) return;
		if (!partnerFanId) return;
		createNote(trimmed);
	};

	return (
		<PunchupDialogue maxWidth={600} onClose={onClose}>
			<DialogIcon>
				<StickyNote size={24} />
			</DialogIcon>
			<DialogTitle>{email}</DialogTitle>
			{fullName && <DialogSubtitle>{fullName}</DialogSubtitle>}
			<PunchupDetailText
				main={t('fan-notes-main', 'Door staff see these notes when scanning any ticket.')}
				secondary={t(
					'fan-notes-secondary',
					'Use notes to capture anything relevant at the door — allergies, VIP status, accessibility needs, or other context. Notes are linked to the attendee, not a specific ticket or event, so they appear whenever this person checks in.'
				)}
				collapsible
			/>
			{notesLoading ? (
				<div className={styles.fetchSpinner}>
					<PunchupSpinner size={32} />
				</div>
			) : (
				<div className={styles.feedScroll}>
					<PunchupFeed>
						{notes.length === 0 ? (
							<PunchupFeedItem>
								<span className={styles.noteText}>{t('fan-notes-empty', 'No notes added yet')}</span>
							</PunchupFeedItem>
						) : (
							notes.map((note) => (
								<PunchupFeedItem
									key={note.id}
									timestamp={new Date(note.updated_at).toLocaleDateString('en-US', {
										year: 'numeric',
										month: 'short',
										day: 'numeric',
									})}
									actions={
										editingNoteId !== note.id ? (
											<div className={styles.noteActions}>
												<button
													type="button"
													className={styles.noteActionBtn}
													onClick={() => handleEditStart(note.id, note.note)}
												>
													{t('edit', 'Edit')}
												</button>
												<button
													type="button"
													className={`${styles.noteActionBtn} ${styles.noteActionBtnDanger}`}
													onClick={() => deleteNote(note.id)}
													disabled={deleting}
												>
													{t('delete', 'Delete')}
												</button>
											</div>
										) : undefined
									}
								>
									{editingNoteId === note.id ? (
										<>
											<EditNoteTextarea value={editingText} onChange={setEditingText} />
											<div className={styles.editActions}>
												<PunchupButton
													variant="primary"
													text={updating ? t('saving', 'Saving…') : t('save', 'Save')}
													onClick={handleEditSave}
													disabled={updating || !editingText.trim()}
												/>
												<PunchupButton
													variant="secondary"
													text={t('cancel', 'Cancel')}
													onClick={handleEditCancel}
													disabled={updating}
												/>
											</div>
										</>
									) : (
										<span className={styles.noteText}>{note.note}</span>
									)}
								</PunchupFeedItem>
							))
						)}
					</PunchupFeed>
				</div>
			)}
			<div className={styles.addNoteSection}>
				<div className={styles.addNoteInputWrapper}>
					{creating && (
						<div className={styles.addNoteLoadingOverlay}>
							<PunchupSpinner size={32} />
						</div>
					)}
					<label className={styles.addNoteLabel} htmlFor="new-fan-note">
						{t('fan-notes-add', 'Add a note')}
					</label>
					<textarea
						id="new-fan-note"
						className={styles.editTextarea}
						placeholder={t('fan-notes-placeholder', 'Nut allergy, VIP, wheelchair access…')}
						value={newNoteText}
						onChange={(e) => setNewNoteText(e.target.value)}
					/>
					{noteSubmitted && !newNoteText.trim() && <p className={styles.inputError}>{t('required', 'Required')}</p>}
				</div>
				<div className={styles.addNoteActions}>
					<PunchupButton variant="secondary" text={t('close', 'Close')} onClick={onClose} disabled={creating} />
					<PunchupButton
						variant="primary"
						text={t('fan-notes-add-btn', 'Add Note')}
						onClick={handleAddNote}
						disabled={creating}
					/>
				</div>
			</div>
		</PunchupDialogue>
	);
};
